import type { Ref, UnwrapNestedRefs } from 'vue';
import type { GIMovie } from 'src/models/geoItem';
import type { IMoviePlayerControlArea } from 'src/models';
import type { MoviePlayerState } from 'src/models/moviePlayer';
import { useMoviePlayerPlaybackVjs } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerPlaybackVjs';
import {
  useMoviePlayerPlaybackControl,
} from 'src/components/lib/MoviePlayer/composables/useMoviePlayerPlaybackControl';
import {
  useMoviePlayerMovieTransition,
} from 'src/components/lib/MoviePlayer/composables/useMoviePlayerMovieTransition';
import { useMoviePlayerSeek } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerSeek';
import { useMoviePlayerTimeUpdate } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerTimeUpdate';

export interface PlaybackCoreConfig {
  refControlArea: Ref<IMoviePlayerControlArea | undefined>;
  // video.jsのprepare完了ポーリング対象vid一覧
  prepareVideoIds: () => string[];
  // ロード完了ポーリング対象vid一覧。frontを先頭にする（体感パフォーマンスのため）
  loadVideoIds: () => string[];
  // 全video要素にprepareVideojs()を呼ぶ
  prepareVideoRefs: () => void;
  // 全video要素にchangeVjsPlayMode()を呼ぶ
  changeVideoPlayMode: (isPlayBackward: boolean) => void;
}

export interface PlaybackCoreContext {
  videoAspectRatio: number;
  onVjsError: (vid: string) => void;
  videoPlayerHasError: () => boolean;
  resetVideoPlayers: () => Promise<void>;
  invalidateCurrentMovie: () => void;
  onVjsPrepared: (vid: string) => void;
  prepareVideoPlayers: () => Promise<void>;
  onVjsTimeUpdate: (rawCurrentTime: number, duration: number) => void;
  onVjsLoadedData: (vid: string) => void;
  playMovie: () => void;
  pauseMovie: () => void;
  playUI: () => void;
  pauseUI: () => void;
  togglePlayPauseUI: () => void;
  tickMovieBackward: () => Promise<void>;
  tickMovieForward: () => Promise<void>;
  emitSeekChange: (msec: number) => void;
  playPreviousMovie: () => Promise<void>;
  playNextMovie: () => Promise<void>;
  setPlaySpeed: (val: number) => void;
  setVolume: (val: number) => void;
  playModeChange: (isPlayBackward: boolean) => void;
  seekBySeekBar: (offsetMsec: number) => void;
  seekMovie: (offsetMsec: number) => Promise<void>;
  seekByTimeText: (timePoint: { dt: Date; dateDisp: string; timeDisp: string }) => Promise<void>;
  setCurrentMovie: (movie: GIMovie, movieIdx: number, playOffsetMsec: number) => Promise<void>;
  setVideoControlPropertiesToDefault: () => void;
  selectUrlForVid: (vid: string) => string;
  findFirstSeekPoint: () => number;
  // vjsPrepared状態の初期化。オーケストレーション層（useMoviePlayerOrchestrationCore）が
  // マウント時にこのAPI経由で初期化することで、playback内部のstate構造への直接依存をなくす。
  initVjsPreparedState: (vids: string[]) => void;
  // 現在の再生速度取得。オーケストレーション層がvideoControlProperties.playSpeedへ
  // 直接アクセスせずに済むようにするgetter。
  getCurrentPlaySpeed: () => number;
  // 現在の再生時刻取得。オーケストレーション層がplayback内部のstate構造へ
  // 直接アクセスせずに済むようにするgetter。
  getCurrentTime: () => number;
}

// 再生コア（play/pause/tick/speed/volume/シーク/video.jsイベント/mgi管理）の共通実装。
// BasicMoviePlayerのuseBasicPlayerPlaybackから、
// vid一覧・video要素操作をconfigとして注入して利用する。
// 内部は責務ごとに vjs → control → movieTransition → (seek / timeUpdate) の一方向依存で分割し、
// 本関数はそれらを合成して従来と同じメンバー集合（+初期化/getter系の追加API）を持つフラットな
// オブジェクトを返すファサードとしてのみ振る舞う。
export function useMoviePlayerPlaybackCore(
  state: UnwrapNestedRefs<MoviePlayerState>,
  config: PlaybackCoreConfig,
): PlaybackCoreContext {
  const vjs = useMoviePlayerPlaybackVjs(state, {
    prepareVideoIds: config.prepareVideoIds,
    prepareVideoRefs: config.prepareVideoRefs,
  });
  const control = useMoviePlayerPlaybackControl(state, {
    refControlArea: config.refControlArea,
    changeVideoPlayMode: config.changeVideoPlayMode,
  });
  const movieTransition = useMoviePlayerMovieTransition(
    state,
    {
      loadVideoIds: config.loadVideoIds,
    },
    control,
    vjs,
  );
  const seek = useMoviePlayerSeek(state, control, movieTransition);
  const timeUpdate = useMoviePlayerTimeUpdate(
    state,
    control,
    movieTransition,
  );

  const getCurrentTime = () => {
    return state.playback.currentTime;
  };

  return {
    videoAspectRatio: vjs.videoAspectRatio,
    onVjsError: vjs.onVjsError,
    videoPlayerHasError: vjs.videoPlayerHasError,
    resetVideoPlayers: movieTransition.resetVideoPlayers,
    invalidateCurrentMovie: movieTransition.invalidateCurrentMovie,
    onVjsPrepared: vjs.onVjsPrepared,
    prepareVideoPlayers: vjs.prepareVideoPlayers,
    onVjsTimeUpdate: timeUpdate.onVjsTimeUpdate,
    onVjsLoadedData: vjs.onVjsLoadedData,
    playMovie: control.playMovie,
    pauseMovie: control.pauseMovie,
    playUI: control.playUI,
    pauseUI: control.pauseUI,
    togglePlayPauseUI: control.togglePlayPauseUI,
    tickMovieBackward: control.tickMovieBackward,
    tickMovieForward: control.tickMovieForward,
    emitSeekChange: control.emitSeekChange,
    playPreviousMovie: movieTransition.playPreviousMovie,
    playNextMovie: movieTransition.playNextMovie,
    setPlaySpeed: control.setPlaySpeed,
    setVolume: control.setVolume,
    playModeChange: control.playModeChange,
    seekBySeekBar: seek.seekBySeekBar,
    seekMovie: seek.seekMovie,
    seekByTimeText: seek.seekByTimeText,
    setCurrentMovie: movieTransition.setCurrentMovie,
    setVideoControlPropertiesToDefault: movieTransition.setVideoControlPropertiesToDefault,
    selectUrlForVid: vjs.selectUrlForVid,
    findFirstSeekPoint: movieTransition.findFirstSeekPoint,
    initVjsPreparedState: vjs.initVjsPreparedState,
    getCurrentPlaySpeed: control.getCurrentPlaySpeed,
    getCurrentTime,
  };
}
